import { Injectable } from '@angular/core';

import { ComplexSearchBarFilter } from './complex-search-bar-filter';
import { ComplexSearchBarComponent, Filter } from './complex-search-bar.component';

export type FormValue = Record<string, any>;
export type SharedFilterMap = Record<string, { withKey: string; withoutKey: string }>;

@Injectable({
  providedIn: 'root'
})
export class SynchronizeComplexSearchBarService {
  /**
   * Synchronise la barre de recherche complexe avec le formulaire
   */
  synchronize(
    searchBar: ComplexSearchBarComponent,
    sharedFormKeys: string[],
    form: FormValue,
    sharedFilterMap: SharedFilterMap,
    filterList: ComplexSearchBarFilter[]
  ): void {
    let filters: Filter[] = [...searchBar.filters()];
    filters = this.removeAllFilters(filters, form, sharedFilterMap);
    const newFilters = sharedFormKeys
      .map((key: string) => this.generateFilter(key, form, sharedFilterMap, searchBar.filters(), filterList))
      .filter(Boolean);

    filters = this.removeInverseFilters(filters, newFilters);
    filters = [...filters, ...newFilters];

    searchBar.filters.set(filters);
  }

  /**
   * Génère une liste de filtre sans les filtres avec la valeur `all`
   */
  removeAllFilters(filters: Filter[], formValue: FormValue, sharedFilterMap: SharedFilterMap): Filter[] {
    return filters.filter((filter: Filter) => {
      const formKey = formValue[filter.apiFilter.filter];
      return (
        formKey !== 'all' ||
        [
          sharedFilterMap[filter.apiFilter.filter].withoutKey,
          sharedFilterMap[filter.apiFilter.filter].withKey
        ].includes(filter.apiFilter.filter)
      );
    });
  }

  /**
   * Génère un filtre à ajouter à la barre de recherche complexe à partir du formulaire
   */
  generateFilter(
    filterKey: string,
    form: FormValue,
    sharedFilterMap: SharedFilterMap,
    filters: Filter[],
    filterList: ComplexSearchBarFilter[]
  ): Filter | null {
    const formValue = form[filterKey];

    if (formValue === 'all') {
      return null;
    }

    const key =
      formValue === true || formValue === 'true'
        ? sharedFilterMap[filterKey].withKey
        : sharedFilterMap[filterKey].withoutKey;
    const found = filters.find((filter: Filter) => filter.apiFilter.filter === key);
    if (found) {
      return null;
    }

    const complexFilter: ComplexSearchBarFilter = filterList.find(
      (filter: ComplexSearchBarFilter) => filter.name === key
    );
    if (!complexFilter) {
      return null;
    }

    return {
      displayedValue: complexFilter.displayedValue(null),
      apiFilter: complexFilter.apiFilter(null)
    };
  }

  /**
   * Supprime les filtres booléens en doublon dans le formulaire
   */
  removeInverseFilters(existingFilters: Filter[], newFilters: Filter[]): Filter[] {
    return existingFilters.filter(
      (existingFilter: Filter) =>
        !newFilters.some((f: Filter) => f.apiFilter.filter === existingFilter.apiFilter.filter)
    );
  }
}
